import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Download, Printer } from 'lucide-react';
import { HeaderActions } from './HeaderActions';

// #265: the header steps down by its OWN width (a CSS container), not the
// viewport's, because the nav and the Tasks panel can leave it narrow on a
// wide screen.
//   >= 880px  back | title + date | nav + actions (one row)
//   640-879   title + date drop to their own row
//   460-639   Prev/Next and Remind me become icons
//   < 460     Remind me, Export and Print fold into a "..." menu
// The last-saved time shows at every width.

interface Props {
  backTo: string;
  backLabel: string;
  title: string;
  meetingDate: string;
  meetingTime?: string | null;
  durationMinutes?: number | null;
  /** Series meetings get Prev/Next; standalone ones do not. */
  nav?: { seriesId: string; prevId?: string | null; nextId?: string | null };
  lastSaved: string | null;
  saving: boolean;
  instanceId: string;
  onExport: () => void;
  onPrint: () => void;
}

export function MeetingHeader(props: Props) {
  const { backTo, backLabel, title, nav, lastSaved, saving, instanceId, onExport, onPrint } = props;

  return (
    <div className="mb-4 no-print @container" data-testid="meeting-header">
      <div className="flex items-center gap-2 min-w-0">
        <Link
          to={backTo}
          title={backLabel}
          className="flex items-center gap-1 min-w-0 max-w-[40%] @min-[880px]:max-w-[22%] text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronLeft size={16} className="flex-shrink-0" />
          <span className="truncate">{backLabel}</span>
        </Link>

        <div className="hidden @min-[880px]:block flex-1 min-w-0 text-center">
          <TitleBlock {...props} />
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
          {nav && (
            <>
              <NavLink to={nav.prevId ? `/meetings/${nav.seriesId}/${nav.prevId}` : null} label="Prev" title="Previous meeting" />
              <NavLink to={nav.nextId ? `/meetings/${nav.seriesId}/${nav.nextId}` : null} label="Next" title="Next meeting" />
              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}
          <HeaderActions
            lastSaved={lastSaved}
            saving={saving}
            actions={[
              { kind: 'remind', key: 'remind', entityType: 'meeting_instance', entityId: instanceId, entityTitle: title },
              { key: 'export', icon: <Download size={14} />, label: 'Export as Markdown', onClick: onExport, iconOnly: true },
              { key: 'print', icon: <Printer size={14} />, label: 'Print / Save as PDF', onClick: onPrint, iconOnly: true },
            ]}
          />
        </div>
      </div>

      <div className="@min-[880px]:hidden text-center mt-2 min-w-0">
        <TitleBlock {...props} />
      </div>
    </div>
  );
}

function TitleBlock({ title, meetingDate, meetingTime, durationMinutes }: Props) {
  return (
    <>
      <h1 className="text-lg @max-[460px]:text-base font-bold text-text-primary truncate" title={title}>
        {title}
      </h1>
      {/* Each part is nowrap, so a squeeze moves the time under the date as a unit. */}
      <div className="flex flex-wrap items-center justify-center gap-x-2 text-xs text-text-muted">
        <span className="flex items-center gap-1 whitespace-nowrap">
          <Calendar size={12} />
          {formatDate(meetingDate)}
        </span>
        {meetingTime && (
          <span className="whitespace-nowrap">
            {formatTime12(meetingTime)}
            {durationMinutes ? ` (${formatDuration(durationMinutes)})` : ''}
          </span>
        )}
      </div>
    </>
  );
}

function NavLink({ to, label, title }: { to: string | null; label: 'Prev' | 'Next'; title: string }) {
  const content = label === 'Prev'
    ? <><ChevronLeft size={14} /><span className="@max-[640px]:hidden">Prev</span></>
    : <><span className="@max-[640px]:hidden">Next</span><ChevronRight size={14} /></>;
  const base = 'flex items-center gap-1 px-2 @max-[640px]:px-1.5 py-1 text-xs rounded transition-colors whitespace-nowrap';
  if (!to) return <span className={`${base} text-text-muted/40`} title={title} aria-disabled="true">{content}</span>;
  return (
    <Link to={to} title={title} aria-label={title} className={`${base} text-text-muted hover:text-text-primary hover:bg-bg-hover`}>
      {content}
    </Link>
  );
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

export function formatTime12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
